// Document.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./utils/firebase";
import { updateData, getUserSettings, updateUserSettings, getDocument } from "./utils/firestoreService";
import { useAuth } from "./utils/authContext";
import { getAuthHeaders } from "./utils/apiUtils";
import { refreshPatientSummary } from "./utils/patientDirectoryService";
import { useFeatureFlags } from "./contexts/FeatureFlagContext";
import EditableTextSection from "./editors/EditableTextSection";
import EditableTableSection from "./editors/EditableTableSection";
import DocumentStartEndTime from "./editors/DocumentStartEndTime";
import DocumentTherapyTiming from "./editors/DocumentTherapyTiming";
import DocumentTreatmentInformation from "./editors/DocumentTreatmentInformation";
import { FileText, Download, ClipboardCopy, FileX, CircleArrowUp, RefreshCcwDot, SquareChevronRight, MessageSquare, MessageSquarePlus, ChevronDown, X } from "lucide-react";
import TranscriptTray from "./components/document/TranscriptTray";
import CommentMarginZone from "./components/document/CommentMarginZone";
import ArchiveDocumentConfirmModal from "./components/document/ArchiveDocumentConfirmModal";
import { useDocumentComments } from "./hooks/useDocumentComments";
import { useDocumentFieldSaves } from "./hooks/useDocumentFieldSaves";
import "./css/comment-margin.css";
import { fetchDocumentData } from "./utils/fetchDocumentData";
import { fetchSavedDocumentType } from "./utils/fetchSavedDocumentType";
import { documentTypeForRecord } from "./utils/documentTypeForRecord";
import { SAVE_STATUS_TIMEOUT_MS } from "./constants/documentEditorConstants";
import ConfirmDocument from "./utils/documentConfirmation";
import { mostRecentTherapyHistory } from "./utils/historyProcessing";
import { useDocumentSections } from "./contexts/DocumentSectionsContext";
import { logAuditEventAsync, AuditActions } from "./utils/auditLogger";
import DocumentApprovalButton from "./components/document/DocumentApprovalButton";
import {
  splitSectionsByTab,
  recordSectionView,
  isEditingLocked,
  handleApprovalOnEdit,
  DEFAULT_APPROVAL_CONFIG,
} from "./utils/approvalUtils";
import { getActiveShareForUser, canPerformAction } from "./utils/sharingService";
import { showNotification } from "./utils/notificationSystem";
import {
  copyToClipboardUtility,
  convertDateStringToDayjs,
  documentDatePickerPatch,
  convertTimeStringToDayjs,
  documentTimePickerPatch,
  hasTranscriptData,
} from "./utils/documentUtils";
import { useSidebar } from "./contexts/SidebarContext";
import { useStreaming } from "./contexts/StreamingContext";
import { reprocessDocument as reprocessDocumentV3, enqueueHistorySummary } from "./utils/processingService";
import ModalPortal from "./utils/ModalPortal";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import { Check } from "lucide-react";

const renderSection = (section, documentData, setDocumentData, documentHistory, copyToClipboard, ClipboardCopy, patientId, docId, key, handlers, userSettings, getSectionEditability, getSectionStatus, isProcessingThisDoc, onSectionView, approvalConfig, isEditLocked, onReprocessSection, onAddSectionComment, onAddTextComment, commentsBySection, onAddRowComment, onSyncAnchors, onPromoteOrphanedComment) => {
  if (section.type === "Confirmation") {
    return null;
  }

  const isEditable = isEditLocked ? false : getSectionEditability(section.dataField);
  const sectionStatus = getSectionStatus(section.dataField);

  // Default open state logic
  const shouldDefaultOpen = () => {
    // During processing: sections auto-open when completed
    if (isProcessingThisDoc) {
      return sectionStatus === "completed";
    }

    // After processing:
    if (userSettings?.collapsedSections === false) {
      return true;
    }

    // If section is completed, show it open
    if (sectionStatus === "completed") {
      return true;
    }

    // Use section's defaultOpen setting (defaults to true if not specified)
    return section.defaultOpen !== false;
  };

  // Track whether this section requires review for approval
  const requiresReview = approvalConfig?.enabled && 
    approvalConfig?.requiredReviewSections?.includes(section.dataField);
  const isReviewed = requiresReview && documentData?.reviewedSections?.[section.dataField]?.viewedAt;

  switch (section.type) {
    case "EditableTextSection":
      return (
        <EditableTextSection
          key={key}
          section={section}
          sectionKey={key || section.title}
          documentData={documentData}
          setDocumentData={setDocumentData}
          dataHistory={documentHistory}
          copyToClipboard={copyToClipboard}
          ClipboardCopy={ClipboardCopy}
          patientId={patientId}
          docId={docId}
          userSettings={userSettings}
          isEditable={isEditable}
          isProcessing={isProcessingThisDoc}
          sectionStatus={sectionStatus}
          defaultOpen={shouldDefaultOpen()}
          onSectionView={onSectionView}
          requiresReview={requiresReview}
          isReviewed={!!isReviewed}
          onReprocessSection={onReprocessSection}
          onAddSectionComment={onAddSectionComment}
          onAddTextComment={onAddTextComment}
          sectionComments={commentsBySection?.get(section.dataField) || []}
          onSyncAnchors={onSyncAnchors}
          onPromoteOrphanedComment={onPromoteOrphanedComment}
        />
      );
    case "EditableTableSection":
      return (
        <EditableTableSection
          isProcessing={isProcessingThisDoc}
          key={key}
          section={section}
          sectionKey={key || section.title}
          data={documentData}
          setDocumentData={setDocumentData}
          dataHistory={documentHistory}
          patientId={patientId}
          docId={docId}
          copyToClipboard={copyToClipboard}
          ClipboardCopy={ClipboardCopy}
          userSettings={userSettings}
          isEditable={isEditable}
          sectionStatus={sectionStatus}
          defaultOpen={shouldDefaultOpen()}
          onSectionView={onSectionView}
          requiresReview={requiresReview}
          isReviewed={!!isReviewed}
          onReprocessSection={onReprocessSection}
          onAddSectionComment={onAddSectionComment}
          onAddRowComment={onAddRowComment}
        />
      );
    case "StartEndTime":
      return <DocumentStartEndTime {...handlers.startEndTimeProps} isEditable={isEditable} />;
    case "TherapyTiming":
      return <DocumentTherapyTiming {...handlers.therapyTimingProps} isEditable={isEditable} />;
    case "TreatmentInformation":
      return <DocumentTreatmentInformation {...handlers.treatmentInfoProps} isEditable={isEditable} />;
    default:
      return null;
  }
};

const Document = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, extendSessionOnAction } = useAuth();
  const { flags } = useFeatureFlags();
  const userId = currentUser?.uid;
  const { patientId, docId, from: navFrom } = location.state || {};

  // State declarations - REMOVED: continueReceived, locationContinueReceived, etc.
  const [documentOld, setDocumentOld] = useState(null);
  const [documentData, setDocumentData] = useState(null);
  const [documentHistory, setDocumentHistory] = useState(null);
  const [personName, setPersonName] = useState("");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [title, setTitle] = useState("");
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  // Rich transcript presence (transcriptionData/session). Pre-recorded recordings
  // persist their transcript to GCS via a session pointer rather than to a
  // document-level text field, so hasTranscriptData() alone misses them — e.g. a
  // recording saved under an upload-format type ("Document Import"). Detect the
  // session subdoc so the transcript button still appears for that edge case.
  const [hasRichTranscript, setHasRichTranscript] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [commentActiveSectionKey, setCommentActiveSectionKey] = useState(null);
  const [commentActiveSectionTitle, setCommentActiveSectionTitle] = useState(null);
  const pendingAnchorRef = useRef(null);
  const contentContainerRef = useRef(null);
  const [trayPosition, setTrayPosition] = useState("right"); // "right" | "bottom" — transcript tray
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [typeState, setTypeState] = useState({ key: null, error: false, canProcess: false });
  const [typeRetry, setTypeRetry] = useState(0);
  const typeKey = `${userId}:${patientId}:${docId}`;
  const canProcessType = typeState.key === typeKey && typeState.canProcess;
  const [copiedIcon, setCopiedIcon] = useState(false);
  const [therapyMinutes, setTherapyMinutes] = useState("");
  const [isEditingTherapyMinutes, setIsEditingTherapyMinutes] = useState(false);
  const [therapyMinutesValidation, setTherapyMinutesValidation] = useState({ isValid: null, message: "" });
  const [therapyType, setTherapyType] = useState("");
  const [isEditingTherapyType, setIsEditingTherapyType] = useState(false);
  const mostRecentTherapy = useMemo(() => mostRecentTherapyHistory(documentHistory || []) || null, [documentHistory]);
  const [settings, setSettings] = useState({});
  const [companySettings, setCompanySettings] = useState({});
  const { setSections: setContextSections } = useDocumentSections();
  const [treatmentSetting, setTreatmentSetting] = useState("");
  const [isEditingTreatmentSetting, setIsEditingTreatmentSetting] = useState(false);
  const [treatingClinician, setTreatingClinician] = useState("");
  const [isEditingTreatingClinician, setIsEditingTreatingClinician] = useState(false);
  const [isReprocessConfirmOpen, setIsReprocessConfirmOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null, 'saving', 'saved', 'error'
  const saveStatusTimeoutRef = useRef(null);
  // Tracks whether the document has been edited during this editor session.
  // Set on every successful save; consumed on unmount to re-ingest into search.
  const dirtyRef = useRef(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [shareOwnerDisplay, setShareOwnerDisplay] = useState(null); // "Shared by …" tooltip on View Only chip
  const [additionalSectionsOpen, setAdditionalSectionsOpen] = useState(false);
  const [qualityBannerDismissed, setQualityBannerDismissed] = useState(false);

  // Track active section reprocess listeners (sectionId → unsubscribe fn)
  const reprocessListenersRef = useRef(new Map());

  // Determine if this document is view-only for the current user.
  //
  // Shared recipient (shareInfo present):
  //   - 'full' access → editable
  //   - Doc they authored → editable if their access level allows editing own docs
  //   - Otherwise → view-only
  //
  // Patient owner (no shareInfo):
  //   - Docs they authored or legacy docs (no createdBy) → editable
  //   - Docs authored by a shared user → view-only
  //     (Firestore rules already block owner writes for view_add_private docs;
  //      the FE blocks the editable surface from showing in the first place so
  //      a clinician sees the correct read/write state regardless of access level.)
  //
  // See: docs/updates/2026-04-29-shared-patient-edit-permissions.md
  const isAuthor = !!documentData?.createdBy && documentData.createdBy === userId;
  const isViewOnly = shareInfo
    ? (!canPerformAction(shareInfo, 'edit') && !(isAuthor && canPerformAction(shareInfo, 'editOwn')))
    : (!!documentData?.createdBy && !isAuthor);
  const canUseDocumentActions = !!documentData && !isViewOnly;
  const actionAccessRef = useRef();
  actionAccessRef.current = { patientId, docId, allowed: canUseDocumentActions };
  const canActOnCurrentDocument = useCallback(() => {
    const access = actionAccessRef.current;
    return access.allowed && access.patientId === patientId && access.docId === docId;
  }, [patientId, docId]);
  useEffect(() => {
    if (!canUseDocumentActions) {
      setIsExportOpen(false);
      setDeleteConfirmOpen(false);
      setCommentActiveSectionKey(null);
      setCommentActiveSectionTitle(null);
      pendingAnchorRef.current = null;
    }
  }, [canUseDocumentActions]);

  // Fetch owner display name for the View Only tooltip (receiving-account view).
  // Falls back to email / "Owner" until the profile-fields card ships.
  // See: docs/updates/2026-04-21-shared-record-show-note-author.md
  useEffect(() => {
    if (!isViewOnly || !patientId) {
      setShareOwnerDisplay(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(`/api/share-owner/${patientId}`, { headers });
        if (!resp.ok) return;
        const data = await resp.json();
        if (!cancelled) setShareOwnerDisplay(data.displayName || null);
      } catch {
        // Non-critical — tooltip just won't show an owner
      }
    })();
    return () => { cancelled = true; };
  }, [isViewOnly, patientId]);

  // Save status handler - shows chip briefly on save
  const showSaveStatus = useCallback((status) => {
    if (saveStatusTimeoutRef.current) {
      clearTimeout(saveStatusTimeoutRef.current);
    }
    setSaveStatus(status);
    if (status === 'saved') {
      // Mark dirty so we re-ingest into search index on editor exit
      dirtyRef.current = true;
      // Extend session on meaningful save action
      extendSessionOnAction?.();
      saveStatusTimeoutRef.current = setTimeout(() => {
        setSaveStatus(null);
      }, SAVE_STATUS_TIMEOUT_MS);
    }
  }, [extendSessionOnAction]);

  // Cleanup save status timeout on unmount + re-ingest to search if edited
  useEffect(() => {
    return () => {
      if (saveStatusTimeoutRef.current) {
        clearTimeout(saveStatusTimeoutRef.current);
      }
      // Fire-and-forget re-ingest on editor exit (only if edits occurred)
      if (dirtyRef.current && patientId && docId) {
        getAuthHeaders().then(headers =>
          fetch('/api/search/ingest', {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'document', patientId, documentId: docId }),
            keepalive: true,
          })
        ).catch(() => {});
      }
    };
  }, [patientId, docId]);

  // Computed values
  const currentDocumentType = useMemo(
    () => documentTypeForRecord(documentTypes, documentData),
    [documentTypes, documentData]
  );
  const currentSections = useMemo(
    () => currentDocumentType?.sections || [],
    [currentDocumentType]
  );

  // Split sections into primary and secondary groups for approval flow
  const { primary: primarySections, secondary: secondarySections } = useMemo(
    () => splitSectionsByTab(currentSections),
    [currentSections]
  );

  const approvalConfig = companySettings.approvalConfig || DEFAULT_APPROVAL_CONFIG;

  const sectionOrder = useMemo(() => {
    if (!currentSections) return [];
    return currentSections
      .filter((s) => s.dataField)
      .map((s) => ({ sectionKey: s.dataField, title: s.title }));
  }, [currentSections]);

  const commentsHook = useDocumentComments({
    patientId,
    documentId: docId,
    enabled: flags?.documentComments,
    readOnly: !canUseDocumentActions,
  });

  // Handler: section comment button clicked → open margin zone + activate inline input
  const handleAddSectionComment = useCallback((sectionKey, sectionTitle) => {
    if (!canActOnCurrentDocument()) return;
    setCommentActiveSectionKey(sectionKey);
    setCommentActiveSectionTitle(sectionTitle);
    setIsCommentsOpen(true);
    setIsTranscriptOpen(false);
    if (userId) updateUserSettings(userId, { commentsMarginOpen: true });
  }, [userId, canActOnCurrentDocument]);

  const clearCommentActiveSection = useCallback(() => {
    setCommentActiveSectionKey(null);
    setCommentActiveSectionTitle(null);
    pendingAnchorRef.current = null;
  }, []);

  // Handler: text selection comment → open margin with anchor data, apply highlight, save comment
  const handleAddTextComment = useCallback((sectionKey, sectionTitle, anchor) => {
    if (!canActOnCurrentDocument()) return;
    setCommentActiveSectionKey(sectionKey);
    setCommentActiveSectionTitle(sectionTitle);
    setIsCommentsOpen(true);
    setIsTranscriptOpen(false);
    if (userId) updateUserSettings(userId, { commentsMarginOpen: true });
    // Store anchor for the inline input to use
    pendingAnchorRef.current = anchor;
  }, [userId, canActOnCurrentDocument]);

  // Handler: row-level comment → open margin with row anchor data
  const handleAddRowComment = useCallback((sectionKey, sectionTitle, rowAnchor) => {
    if (!canActOnCurrentDocument()) return;
    setCommentActiveSectionKey(sectionKey);
    setCommentActiveSectionTitle(sectionTitle);
    setIsCommentsOpen(true);
    setIsTranscriptOpen(false);
    if (userId) updateUserSettings(userId, { commentsMarginOpen: true });
    pendingAnchorRef.current = rowAnchor;
  }, [userId, canActOnCurrentDocument]);

  const { sidebarCollapsed, isProcessing, processingDocId } = useSidebar();
  const { isStreaming, setIsStreaming } = useStreaming();

  // Simple check: is THIS document currently processing?
  const isProcessingThisDoc = isProcessing && processingDocId === docId;

  // Get section processing status
  const getSectionStatus = useCallback(
    (sectionKey) => {
      if (!documentData) return "pending";

      const sectionData = documentData[sectionKey];

      // Check if this specific section has been processed
      if (sectionData?.processingStatus === true) {
        return "completed";
      }

      // Show as processing if document is processing AND section isn't complete
      if (isProcessingThisDoc) {
        return "processing";
      }

      return "pending";
    },
    [documentData, isProcessingThisDoc]
  );

  // Get section editability
  const getSectionEditability = useCallback(
    (sectionKey) => {
      if (!documentData) return true;

      // View-only users cannot edit any sections
      if (isViewOnly) return false;

      // If not processing this document, always editable
      if (!isProcessingThisDoc) return true;

      // During processing: only editable if section has processingStatus === true
      const sectionData = documentData[sectionKey];
      return sectionData?.processingStatus === true;
    },
    [documentData, isProcessingThisDoc, isViewOnly]
  );

  // Track section views for approval review requirements
  const handleSectionView = useCallback(
    (dataField) => {
      if (isViewOnly) return;
      if (!approvalConfig?.enabled || !patientId || !docId || !userId) return;
      if (!approvalConfig.requiredReviewSections?.includes(dataField)) return;
      // Skip if already reviewed
      if (documentData?.reviewedSections?.[dataField]?.viewedAt) return;
      
      recordSectionView(patientId, docId, dataField, userId);
      // Optimistic local update
      setDocumentData(prev => ({
        ...prev,
        reviewedSections: {
          ...prev?.reviewedSections,
          [dataField]: { viewedAt: new Date(), viewedBy: userId },
        },
      }));
    },
    [isViewOnly, approvalConfig, patientId, docId, userId, documentData?.reviewedSections, setDocumentData]
  );

  // Check if editing is locked (approval lock mode)
  const isEditLocked = useMemo(
    () => isEditingLocked(documentData, approvalConfig),
    [documentData, approvalConfig]
  );
  const { saveField, waitForSaves } = useDocumentFieldSaves({
    patientId, docId, documentData, setDocumentData,
    canEdit: canUseDocumentActions && !isEditLocked,
    onStatus: showSaveStatus,
    onCommitted: () => { dirtyRef.current = true; },
  });

  const handleReprocessDocument = useCallback(async () => {
    setIsReprocessConfirmOpen(false);
    if (!canProcessType || !canActOnCurrentDocument()) return;

    try {
      const result = await reprocessDocumentV3({
        documentId: docId,
        patientId,
        mode: "full",
      });

      if (!result.success) {
        console.error("[handleReprocessDocument] v3 reprocess failed:", result);
        showNotification(result.error || "Failed to reprocess document", "error", 5000);
      }
    } catch (error) {
      console.error("[handleReprocessDocument] Error initiating reprocess:", {
        error: error.message,
        stack: error.stack,
        patientId,
        docId,
      });
      showNotification("Error starting reprocess. Please try again.", "error", 5000);
    }
  }, [patientId, docId, canProcessType, canActOnCurrentDocument]);

  // Handle reprocessing a single section (AppShell v3 only)
  const handleReprocessSection = useCallback(async (sectionId, sectionName) => {
    if (!canProcessType || !canActOnCurrentDocument()) return;
    try {
      const { reprocessSection } = await import("./utils/processingService");
      
      const result = await reprocessSection({
        documentId: docId,
        patientId,
        sectionId,
      });

      if (result.success) {
        // Start a real-time listener to silently refresh when section completes.
        // Clean up any existing listener for this section first.
        reprocessListenersRef.current.get(sectionId)?.();

        const docRef = doc(db, `patients/${patientId}/documents`, docId);
        let seenProcessing = false;
        const unsubscribe = onSnapshot(docRef, (snapshot) => {
          if (!snapshot.exists()) return;
          const freshData = snapshot.data();
          const sectionData = freshData[sectionId];

          // Wait until we see processingStatus: true (set by the reprocess route),
          // then detect completion when it flips to false (set by processSection).
          if (sectionData?.processingStatus === true) {
            seenProcessing = true;
          }
          if (seenProcessing && sectionData && sectionData.processingStatus === false) {
            getDocument(`patients/${patientId}/documents`, docId).then(data => {
              if (data) setDocumentData(data);
            }).catch(error => console.warn('Could not refresh completed section:', error.message));
            unsubscribe();
            reprocessListenersRef.current.delete(sectionId);
          }
        });

        reprocessListenersRef.current.set(sectionId, unsubscribe);
      } else {
        console.error("[handleReprocessSection] Failed:", result);
        showNotification(result.error || "Failed to reprocess section", "error", 5000);
      }
    } catch (error) {
      console.error("[handleReprocessSection] Error:", error);
      showNotification("Error starting section reprocess", "error", 5000);
    }
  }, [docId, patientId, canProcessType, canActOnCurrentDocument]);

  // Clean up reprocess listeners on unmount
  useEffect(() => {
    return () => {
      reprocessListenersRef.current.forEach((unsub) => unsub());
      reprocessListenersRef.current.clear();
    };
  }, []);

  // Watch for rich transcript data so the transcript button appears even when the
  // flat transcript was never mirrored to a document-level field (pre-recorded
  // recordings store it in GCS behind the transcriptionData/session pointer). A
  // listener (not a one-shot read) so the button lights up live once assembly
  // writes the session doc after a recording finishes processing.
  useEffect(() => {
    if (!patientId || !docId) {
      setHasRichTranscript(false);
      return;
    }
    const sessionRef = doc(db, `patients/${patientId}/documents/${docId}/transcriptionData/session`);
    const unsubscribe = onSnapshot(
      sessionRef,
      (snap) => setHasRichTranscript(snap.exists()),
      () => setHasRichTranscript(false)
    );
    return unsubscribe;
  }, [patientId, docId]);

  // Effects
  useEffect(() => {
    if (!patientId || !docId) {
      console.error(`[Document] Missing required parameters - redirecting`, { patientId, docId });
      navigate("/patient", { replace: true });
      return;
    }

    // Reset per-document state on patient/doc change so we never render the
    // previous patient's fields under the new patient's header (cross-patient
    // PHI display defect — see docs/updates/2026-05-06-document-editor-wrong-
    // patient-data-after-alerts-switch.md). The component renders the loading
    // shell when documentData is null.
    setDocumentData(null);
    setDocumentHistory(null);
    setDocumentOld(null);
    setPersonName("");
    setTitle("");
    setShareInfo(null);
    setShareOwnerDisplay(null);

    let cancelled = false;
    (async () => {
      const result = await fetchDocumentData(patientId, docId, setPersonName, setDocumentData, setTitle, setDocumentHistory, setDocumentOld);
      if (cancelled) return;
      if (!result || result.notFound) {
        // Persistent bell-tray entry (no auto-dismiss, no popup). Redirect is
        // immediate, so a transient toast is easy to miss — keep the signal
        // in the Notifications dropdown until the user dismisses it.
        showNotification(
          "This document has been deleted and is no longer available.",
          "warning",
          0,
          { key: `deleted-doc-${docId}`, priority: false }
        );
        navigate("/dashboard", { replace: true });
      }
    })();

    // Check if this patient is shared with the current user
    const fetchShareInfo = async () => {
      if (!userId) return;
      try {
        const share = await getActiveShareForUser(userId, patientId);
        setShareInfo(share);
      } catch (error) {
        console.error('[Document] Error fetching share info:', error);
      }
    };
    fetchShareInfo();
    
    // Audit log: document view
    logAuditEventAsync(AuditActions.DOCUMENT_VIEW, {
      resourceType: 'document',
      resourceId: docId,
      patientId: patientId,
    });

    // Listen for patient edits (e.g. name change) to update header without refresh
    const handlePatientUpdate = (e) => {
      if (e.detail?.patientId === patientId && e.detail?.patientData) {
        const pd = e.detail.patientData;
        const updatedName = [pd.firstName, pd.lastName].filter(Boolean).join(" ");
        if (updatedName) setPersonName(updatedName);
      }
    };
    window.addEventListener("patientUpdated", handlePatientUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener("patientUpdated", handlePatientUpdate);
    };
  }, [patientId, docId, navigate, userId]);

  useEffect(() => {
    let active = true;
    setDocumentTypes([]);
    setTypeState({ key: typeKey, error: false, canProcess: false });
    if (userId && patientId && docId) {
      fetchSavedDocumentType(patientId, docId).then(({ documentType, canProcess }) => {
        if (!active) return;
        setDocumentTypes([documentType]);
        setTypeState({ key: typeKey, error: false, canProcess: canProcess === true });
      }).catch(() => {
        if (active) setTypeState({ key: typeKey, error: true, canProcess: false });
      });
    }
    return () => { active = false; };
  }, [userId, patientId, docId, typeKey, typeRetry]);

// Fetch user settings and company settings independently of the saved-note layout.
useEffect(() => {
  if (!userId) return;

  const fetchData = async () => {
    // Fetch user settings first
    const userSettings = await getUserSettings(userId);
    if (userSettings) {
      setSettings(userSettings);
      if (userSettings.trayPosition) {
        setTrayPosition(userSettings.trayPosition);
      } else if (userSettings.transcriptTrayPosition) {
        // Migrate from legacy per-tray setting
        setTrayPosition(userSettings.transcriptTrayPosition);
      }
      if (userSettings.commentsMarginOpen) {
        setIsCommentsOpen(true);
      }
      if (userSettings.transcriptTrayOpen) {
        setIsTranscriptOpen(true);
      }
    }
    
    // Fetch the full user document to get companyId
    try {
      const userDoc = await getDocument('users', userId);
      if (userDoc?.companyId) {
        const company = await getDocument('companies', userDoc.companyId);
        if (company) {
          setCompanySettings({
            riskAssessmentEnabled: company.riskAssessmentEnabled !== undefined ? company.riskAssessmentEnabled : true,
            companyName: company.name || '',
            approvalConfig: company.approvalConfig || DEFAULT_APPROVAL_CONFIG,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }

  };

  fetchData();
}, [userId]);

  useEffect(() => {
    if (currentSections && setContextSections) {
      setContextSections(currentSections);
    }
    return () => {
      if (setContextSections) {
        setContextSections([]);
      }
    };
  }, [currentSections, setContextSections]);

  const handleBack = useCallback(() => {
    navigate("/patient", { state: { patientId: patientId, from: navFrom } });
  }, [navigate, patientId, navFrom]);

  const copyToClipboard = useCallback(async (content, isHtml = false) => {
    await copyToClipboardUtility(content, setCopiedIcon, isHtml);
  }, []);

  const handleTitleEdit = useCallback(() => {
    if (!canActOnCurrentDocument() || isEditLocked) return;
    setIsEditingTitle(true);
  }, [canActOnCurrentDocument, isEditLocked]);

  const handleDeleteDocument = useCallback(
    async (docId) => {
      if (!canActOnCurrentDocument()) return false;
      if (!(await waitForSaves())) throw Object.assign(new Error('Save changes successfully before archiving this document.'), { code: 'document-save-failed' });
      if (!canActOnCurrentDocument()) return false;
      await updateData(`patients/${patientId}/documents`, docId, {
        isArchived: true,
        modifiedAt: new Date(),
      });
      await refreshPatientSummary(userId, patientId).catch((error) => {
        console.warn("[Document] Failed to refresh list summary after archive:", error.message);
      });
      logAuditEventAsync(AuditActions.DOCUMENT_ARCHIVE, {
        resourceType: 'document',
        resourceId: docId,
        patientId: patientId,
      });
      // Remove from search index (fire-and-forget)
      getAuthHeaders().then(headers =>
        fetch(`/api/search/index/doc_${patientId}_${docId}`, { method: 'DELETE', headers })
      ).catch(() => {});
      if (canActOnCurrentDocument()) navigate("/patient", { state: { patientId: patientId, from: navFrom } });
    },
    [patientId, userId, navigate, navFrom, canActOnCurrentDocument, waitForSaves]
  );

  const handleTreatmentInfoSave = useCallback(
    async (field, value) => {
      if (isViewOnly || isEditLocked) return;
      let setIsEditingSpecific;

      if (field === "treatmentSetting") {
        setIsEditingSpecific = setIsEditingTreatmentSetting;
      } else if (field === "treatingClinician") {
        setIsEditingSpecific = setIsEditingTreatingClinician;
      } else {
        console.error("Invalid field for handleTreatmentInfoSave:", field);
        return;
      }

      return saveField(field, () => ({ [field]: value }), {
        onSuccess: () => setIsEditingSpecific(false),
      });
    },
    [saveField, isViewOnly, isEditLocked]
  );

  const handleTitleSave = useCallback(async () => {
    return saveField('title', () => ({ title }), {
      onSuccess: () => setIsEditingTitle(false),
    });
  }, [saveField, title]);

  const handleTitleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleTitleSave();
      } else if (e.key === "Escape") {
        setIsEditingTitle(false);
        setTitle(documentData?.title || "");
      }
    },
    [handleTitleSave, documentData?.title]
  );

  const handleTherapyMinutesChange = useCallback((e) => {
    const value = e.target.value;
    setTherapyMinutes(value);

    // If empty, clear validation (empty is allowed)
    if (!value || value.trim() === "") {
      setTherapyMinutesValidation({ isValid: null, message: "" });
      return;
    }

    // Check for leading zeros (but allow just "0")
    if (value.length > 1 && value.startsWith("0") && value[1] !== ".") {
      setTherapyMinutesValidation({
        isValid: false,
        message: "Remove leading zeros",
      });
      return;
    }

    // Check if it's a valid number
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      setTherapyMinutesValidation({
        isValid: false,
        message: "Please enter a valid number",
      });
      return;
    }

    // Check for negative numbers
    if (numValue < 0) {
      setTherapyMinutesValidation({
        isValid: false,
        message: "Minutes cannot be negative",
      });
      return;
    }

    // Check maximum (480 minutes = 8 hours)
    const maxMinutes = 480;
    if (numValue > maxMinutes) {
      setTherapyMinutesValidation({
        isValid: false,
        message: `Minutes cannot exceed ${maxMinutes}`,
      });
      return;
    }

    // Valid!
    setTherapyMinutesValidation({ isValid: true, message: "" });
  }, []);

  const handleTherapyTimeSave = useCallback(
    async (field, value) => {
      if (isViewOnly || isEditLocked) return;
      let setIsEditingSpecific;

      if (field === "therapyMinutes") {
        // Check validation before saving
        if (therapyMinutesValidation.isValid === false) {
          return; // Don't save invalid data
        }
        setIsEditingSpecific = setIsEditingTherapyMinutes;
      } else if (field === "therapyType") {
        setIsEditingSpecific = setIsEditingTherapyType;
      } else {
        console.error("Invalid field for handleTherapyTimeSave:", field);
        return;
      }
      return saveField(field, () => ({ [field]: value }), {
        onSuccess: () => setIsEditingSpecific(false),
      });
    },
    [saveField, therapyMinutesValidation, isViewOnly, isEditLocked]
  );

  // State for date/time picker values
  const [datePickerValue, setDatePickerValue] = useState(null);
  const [startTimePickerValue, setStartTimePickerValue] = useState(null);
  const [endTimePickerValue, setEndTimePickerValue] = useState(null);
  const [treatmentStartDateValue, setTreatmentStartDateValue] = useState(null);
  const [treatmentEndDateValue, setTreatmentEndDateValue] = useState(null);
  
  // Sync picker values with document data
  // Track specific field values to avoid unnecessary re-syncs on unrelated documentData changes
  const docStartTime = documentData?.startTime;
  const docEndTime = documentData?.endTime;
  const docDocumentDate = documentData?.documentDate;
  const docStartDate = documentData?.startDate;
  const docEndDate = documentData?.endDate;
  
  useEffect(() => {
    setDatePickerValue(convertDateStringToDayjs(docDocumentDate));
  }, [docDocumentDate]);
  
  useEffect(() => {
    setStartTimePickerValue(convertTimeStringToDayjs(docStartTime));
  }, [docStartTime]);
  
  useEffect(() => {
    setEndTimePickerValue(convertTimeStringToDayjs(docEndTime));
  }, [docEndTime]);
  
  useEffect(() => {
    setTreatmentStartDateValue(convertDateStringToDayjs(docStartDate));
  }, [docStartDate]);
  
  useEffect(() => {
    setTreatmentEndDateValue(convertDateStringToDayjs(docEndDate));
  }, [docEndDate]);

  // Wrapper for date picker saves with status
  const handleDatePickerSave = useCallback(async (newValue, dateField = 'documentDate') => {
    const restoreValue = {
      documentDate: setDatePickerValue,
      startDate: setTreatmentStartDateValue,
      endDate: setTreatmentEndDateValue,
    }[dateField];
    return saveField(dateField, () => documentDatePickerPatch(newValue, dateField), {
      onError: stored => restoreValue?.(convertDateStringToDayjs(stored)),
    });
  }, [saveField]);

  // Wrapper for time picker saves with status
  const handleTimePickerSave = useCallback(async (timeField, newValue) => {
    const restoreValue = timeField === 'startTime' ? setStartTimePickerValue : setEndTimePickerValue;
    return saveField(timeField, () => documentTimePickerPatch(timeField, newValue), {
      onError: stored => restoreValue(convertTimeStringToDayjs(stored)),
    });
  }, [saveField]);

  // Props for extracted section components
  const sectionHandlers = {
    startEndTimeProps: {
      datePickerValue, setDatePickerValue,
      startTimePickerValue, setStartTimePickerValue,
      endTimePickerValue, setEndTimePickerValue,
      handleDatePickerSave, handleTimePickerSave,
    },
    therapyTimingProps: {
      documentData,
      isEditingTherapyMinutes, setIsEditingTherapyMinutes,
      therapyMinutes, setTherapyMinutes,
      therapyMinutesValidation, setTherapyMinutesValidation,
      handleTherapyMinutesChange, handleTherapyTimeSave,
      isEditingTherapyType, setIsEditingTherapyType,
      therapyType, setTherapyType,
      mostRecentTherapy,
    },
    treatmentInfoProps: {
      documentData,
      treatmentStartDateValue, setTreatmentStartDateValue,
      treatmentEndDateValue, setTreatmentEndDateValue,
      handleDatePickerSave,
      isEditingTreatmentSetting, setIsEditingTreatmentSetting,
      treatmentSetting, setTreatmentSetting,
      handleTreatmentInfoSave,
      isEditingTreatingClinician, setIsEditingTreatingClinician,
      treatingClinician, setTreatingClinician,
    },
  };

  const handleExportDocument = useCallback(async () => {
    if (!canActOnCurrentDocument() || !(await waitForSaves()) || !canActOnCurrentDocument()) return;
    try {
      const ifCurrent = setter => value => { if (canActOnCurrentDocument()) setter(value); };
      await fetchDocumentData(patientId, docId, ifCurrent(setPersonName), ifCurrent(setDocumentData), ifCurrent(setTitle), ifCurrent(setDocumentHistory), ifCurrent(setDocumentOld));
      if (!canActOnCurrentDocument()) return;
      setIsExportOpen(true);

      // Advanced History: exporting a note is the "reviewed content" signal —
      // enqueue a post-export History Summary (coalesced + delayed server-side).
      // Fire-and-forget; failures must never affect the export flow.
      if (flags?.advancedHistory && patientId) {
        enqueueHistorySummary({ patientId, triggeredByDocId: docId });
      }
    } catch (error) {
      console.error("Error fetching document data:", error);
    }
  }, [patientId, docId, flags?.advancedHistory, canActOnCurrentDocument, waitForSaves]);

  // Listen for triggerExport events from Activity Bar action handlers
  useEffect(() => {
    const handleTriggerExport = (e) => {
      if (!canActOnCurrentDocument()) return;
      const format = e.detail?.format;
      if (format === "print") {
        window.print();
      } else {
        // pdf, docx, clipboard — open the export modal
        handleExportDocument();
      }
    };
    window.addEventListener("triggerExport", handleTriggerExport);
    return () => window.removeEventListener("triggerExport", handleTriggerExport);
  }, [handleExportDocument, canActOnCurrentDocument]);

  // Listen for triggerExportNote from Activity Bar (Export Note chip)
  useEffect(() => {
    const handler = () => handleExportDocument();
    window.addEventListener("triggerExportNote", handler);
    return () => window.removeEventListener("triggerExportNote", handler);
  }, [handleExportDocument]);

  // Listen for triggerDeleteDocument from Activity Bar (Delete Document chip)
  useEffect(() => {
    const handler = () => { if (canActOnCurrentDocument()) setDeleteConfirmOpen(true); };
    window.addEventListener("triggerDeleteDocument", handler);
    return () => window.removeEventListener("triggerDeleteDocument", handler);
  }, [canActOnCurrentDocument]);

  const documentComplete = useCallback(() => {
    setIsExportOpen(false);
    navigate("/patient", { state: { patientId: patientId, from: navFrom } });
  }, [patientId, navigate, navFrom]);

  const scrollToTop = useCallback((e) => {
    // Find the real scrollport by walking up from the click target so nested
    // scrolling areas continue to work if the layout changes.
    // See docs/updates/2026-04-20-scroll-to-top-button-not-scrolling.md
    let el = e?.currentTarget?.parentElement || null;
    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      const overflowY = style.overflowY;
      if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) {
        el.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      el = el.parentElement;
    }
    // Fall back to the main content scroller, then the document viewport.
    const appShellContent = document.querySelector(".app-shell-content");
    if (appShellContent && appShellContent.scrollHeight > appShellContent.clientHeight) {
      appShellContent.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    document.documentElement?.scrollTo?.({ top: 0, behavior: "smooth" });
  }, []);

  // Return loading state if document is not ready
  if (!documentData || typeState.key !== typeKey || !currentDocumentType || typeState.error) {
    return (
      <div className={`page-container${sidebarCollapsed ? " wide" : ""}`}>
        <div className={`header-container elevated-header${sidebarCollapsed ? " wide" : ""}`}>
          <Tooltip title="Go back" arrow>
            <button className="back-button" onClick={handleBack} aria-label="Go back">
              <SquareChevronRight size={18} style={{ transform: 'scaleX(-1)' }} />
            </button>
          </Tooltip>
          <div className="page-title">{typeState.error ? 'Document unavailable' : 'Loading document...'}</div>
        </div>
        <div className="content-container">
          <div className="loading-container">
            {typeState.error ? <div role="alert">
              <p>This document's layout couldn't be loaded. Please try again.</p>
              <button className="button-update" onClick={() => setTypeRetry(value => value + 1)}>Try again</button>
            </div> : <div className="loading-spinner"></div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`page-container${sidebarCollapsed ? " wide" : ""}`}>
      {/* Page header — collapses via activity-bar-takeover CSS when Activity Bar is open */}
      <div className={`header-container elevated-header${sidebarCollapsed ? " wide" : ""}`}>
        <div className="header-left">
          <Tooltip title="Go back" arrow>
            <button className="back-button" onClick={handleBack} aria-label="Go back">
              <SquareChevronRight size={18} style={{ transform: 'scaleX(-1)' }} />
            </button>
          </Tooltip>
          <div className="page-title userback-ignore">
            Document Review: <strong>{personName}</strong>
            {isViewOnly && (
              <Tooltip
                title={shareOwnerDisplay ? `Shared by ${shareOwnerDisplay}` : ""}
                arrow
                disableHoverListener={!shareOwnerDisplay}
              >
                <Chip
                  label="View Only"
                  size="small"
                  variant="outlined"
                  sx={{ ml: 1, fontSize: 'var(--text-xs)', height: '20px' }}
                />
              </Tooltip>
            )}
          </div>
        </div>

        <div className="header-right">
          {/* Save status chip */}
          {saveStatus && (
            <Chip
              icon={saveStatus === 'saved' ? <Check size={14} /> : undefined}
              label={saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Error'}
              size="small"
              color={saveStatus === 'error' ? 'error' : 'default'}
              variant="outlined"
              sx={{ mr: 1, transition: 'opacity 0.3s', opacity: saveStatus ? 1 : 0 }}
            />
          )}
          
          {/* Document approval button */}
          <DocumentApprovalButton
            documentData={documentData}
            setDocumentData={setDocumentData}
            approvalConfig={approvalConfig}
            currentSections={currentSections}
            patientId={patientId}
            docId={docId}
            userId={userId}
            showSaveStatus={showSaveStatus}
            isViewOnly={isViewOnly}
          />

          {!isViewOnly &&
            (currentDocumentType.format === "audio" || currentDocumentType.format === "summary") &&
            documentOld !== true && (
              <div className="header-button" onClick={handleExportDocument}>
                <Tooltip title="Export Note" arrow>
                  <Download className="header-icon" />
                </Tooltip>
                Export Note
              </div>
          )}

          {!isViewOnly && canProcessType && hasTranscriptData(documentData, 1024) && (documentData?.status === "streaming" || documentData?.status === "error") && (
            <div className="header-button" onClick={() => setIsReprocessConfirmOpen(true)}>
              <Tooltip title="Document incomplete - reprocess" arrow>
                <RefreshCcwDot className="header-icon" />
              </Tooltip>
              Reprocess
            </div>
          )}

          {!isViewOnly && (hasTranscriptData(documentData) || hasRichTranscript) && (
            <div className={`header-button${isTranscriptOpen ? " transcript-active" : ""}`} onClick={() => {
              setIsTranscriptOpen(prev => {
                const next = !prev;
                if (next) setIsCommentsOpen(false);
                if (userId) updateUserSettings(userId, { transcriptTrayOpen: next });
                return next;
              });
            }} role="button" tabIndex={0} aria-label={isTranscriptOpen ? "Close Transcript" : "View Transcript"} onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setIsTranscriptOpen(prev => {
                  const next = !prev;
                  if (userId) updateUserSettings(userId, { transcriptTrayOpen: next });
                  return next;
                });
              }
            }}>
              <Tooltip title={isTranscriptOpen ? "Close Transcript" : "View Transcript"} arrow>
                <FileText className="header-icon" />
              </Tooltip>
            </div>
          )}

          {flags?.documentComments && (
            <div
              className={`header-button${isCommentsOpen ? " comments-active" : ""}`}
              style={{ position: 'relative' }}
              onClick={() => {
                setIsCommentsOpen(prev => {
                  const next = !prev;
                  if (next) setIsTranscriptOpen(false);
                  if (userId) updateUserSettings(userId, { commentsMarginOpen: next });
                  return next;
                });
              }}
              role="button"
              tabIndex={0}
              aria-label={isCommentsOpen ? "Close Comments" : "View Comments"}
              onKeyDown={(e) => e.key === "Enter" && setIsCommentsOpen(prev => !prev)}
            >
              <Tooltip title={isCommentsOpen ? "Close Comments" : "Comments"} arrow>
                <MessageSquare className="header-icon" />
              </Tooltip>
              {commentsHook.commentCount > 0 && (
                <span className="comment-count-badge">{commentsHook.commentCount}</span>
              )}
            </div>
          )}

          {!isViewOnly && (
            <div className="header-button" onClick={() => setDeleteConfirmOpen(true)} role="button" tabIndex={0} aria-label="Delete Note" onKeyDown={(e) => e.key === 'Enter' && setDeleteConfirmOpen(true)}>
              <Tooltip title="Delete Note" arrow>
                <FileX className="header-icon" />
              </Tooltip>
            </div>
          )}
        </div>
      </div>

      {/* Content + Tray wrapper — flex layout for transcript or comments side panels */}
      <div className={
        isTranscriptOpen ? `transcript-tray-layout tray-${trayPosition}` :
        isCommentsOpen ? "comment-margin-layout" :
        undefined
      }>
        {documentOld === false ? (
          <div ref={contentContainerRef} className="content-container userback-ignore" style={{ position: 'relative' }}>
            {/* Friendly "system busy" banner — processing paused on a transient
                Vertex rate-limit. No technical/quota wording; one-click retry. */}
            {documentData?.status === "error" && documentData?.processingErrorCode === "busy" && (
              <div
                role="alert"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  margin: "0 0 16px",
                  padding: "12px 16px",
                  background: "var(--color-warning-light, #fff8e1)",
                  border: "1px solid var(--color-warning, #f5b400)",
                  borderRadius: 8,
                  fontSize: "var(--text-base)",
                }}
              >
                <span>
                  This note couldn't finish processing right now — our system is briefly busy.
                  Please try again in a few minutes.
                </span>
                {!isViewOnly && canProcessType && (
                  <button
                    type="button"
                    onClick={handleReprocessDocument}
                    style={{
                      flexShrink: 0,
                      padding: "6px 16px",
                      background: "var(--color-medium-blue, #2563eb)",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontSize: "var(--text-base)",
                    }}
                  >
                    Retry
                  </button>
                )}
              </div>
            )}
            {/* Document header */}
            <div className="document-title userback-ignore">
              {isEditingTitle && canUseDocumentActions && !isEditLocked ? (
                <span style={{ width: '100%' }}>
                  <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} onBlur={handleTitleSave} onKeyDown={handleTitleKeyPress} autoFocus className="document-title-input" />
                </span>
              ) : (
                <span onClick={canUseDocumentActions && !isEditLocked ? handleTitleEdit : undefined} style={isViewOnly || isEditLocked ? { cursor: 'default' } : undefined}>{title}</span>
              )}
              {flags?.documentComments && !isViewOnly && (
                <Tooltip title="Add document comment" arrow>
                  <span
                    className="document-comment-btn"
                    role="button"
                    tabIndex={0}
                    aria-label="Add document comment"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddSectionComment(null, title);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.stopPropagation();
                        handleAddSectionComment(null, title);
                      }
                    }}
                  >
                    <MessageSquarePlus size={14} />
                  </span>
                </Tooltip>
              )}
            </div>

            {/* Phase 4: Audio quality warning banner — simple dismissible summary */}
            {!qualityBannerDismissed && documentData?.audioQualityLabel && (documentData.audioQualityLabel === "fair" || documentData.audioQualityLabel === "poor") && (
              <div className={`audio-quality-banner audio-quality-banner--${documentData.audioQualityLabel}`}>
                <span className="audio-quality-banner-icon">
                  {documentData.audioQualityLabel === "poor" ? "⚠️" : "ℹ️"}
                </span>
                <span className="audio-quality-banner-text">
                  Audio quality issues detected during recording.
                </span>
                <button
                  className="audio-quality-banner-dismiss"
                  onClick={() => setQualityBannerDismissed(true)}
                  aria-label="Dismiss audio quality warning"
                >
                  <X size={14} />
                </button>
              </div>
            )}

            {primarySections.map((section, index) => {
              const key = section.dataField || `section-${index}`;
              return (
                <div key={`${section.title}-${index}`} id={section.dataField || undefined}>
                  {renderSection(section, documentData, setDocumentData, documentHistory, copyToClipboard, ClipboardCopy, patientId, docId, key, sectionHandlers, settings, getSectionEditability, getSectionStatus, isProcessingThisDoc, handleSectionView, approvalConfig, isEditLocked, canProcessType ? handleReprocessSection : undefined, flags?.documentComments ? handleAddSectionComment : undefined, undefined /* text-anchored comments disabled — see document-comments.md */, commentsHook.commentsBySection, flags?.documentComments ? handleAddRowComment : undefined, commentsHook.updateAnchors, commentsHook.promoteToSectionComment)}
                </div>
              );
            })}

            {/* Additional Sections — secondary/review sections in a collapsible group */}
            {secondarySections.length > 0 && (
              <div className="additional-sections-group">
                <div
                  className="additional-sections-header"
                  onClick={() => setAdditionalSectionsOpen(prev => !prev)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={additionalSectionsOpen}
                  aria-label={`Additional Sections (${secondarySections.length})`}
                  onKeyDown={(e) => e.key === 'Enter' && setAdditionalSectionsOpen(prev => !prev)}
                >
                  <ChevronDown
                    size={16}
                    style={{
                      transform: additionalSectionsOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 0.2s ease',
                      marginRight: '8px',
                    }}
                  />
                  <span className="additional-sections-title">
                    Additional Sections ({secondarySections.length})
                  </span>
                  {approvalConfig?.enabled && (() => {
                    const reviewable = secondarySections.filter(s =>
                      approvalConfig.requiredReviewSections?.includes(s.dataField)
                    );
                    if (reviewable.length === 0) return null;
                    const reviewed = reviewable.filter(s =>
                      documentData?.reviewedSections?.[s.dataField]?.viewedAt
                    );
                    return (
                      <span className="additional-sections-review-count">
                        {reviewed.length}/{reviewable.length} reviewed
                      </span>
                    );
                  })()}
                </div>
                {additionalSectionsOpen && secondarySections.map((section, index) => {
                  const key = section.dataField || `secondary-section-${index}`;
                  return (
                    <div key={`secondary-${section.title}-${index}`} id={section.dataField || undefined}>
                      {renderSection(section, documentData, setDocumentData, documentHistory, copyToClipboard, ClipboardCopy, patientId, docId, key, sectionHandlers, settings, getSectionEditability, getSectionStatus, isProcessingThisDoc, handleSectionView, approvalConfig, isEditLocked, canProcessType ? handleReprocessSection : undefined, flags?.documentComments ? handleAddSectionComment : undefined, undefined /* text-anchored comments disabled — see document-comments.md */, commentsHook.commentsBySection, flags?.documentComments ? handleAddRowComment : undefined, commentsHook.updateAnchors, commentsHook.promoteToSectionComment)}
                    </div>
                  );
                })}
              </div>
            )}

            <Tooltip title="Scroll to top" arrow>
              <button className="scroll-to-top-button" onClick={scrollToTop} aria-label="Scroll to top">
                <CircleArrowUp />
              </button>
            </Tooltip>
          </div>
        ) : documentOld === true ? (
          <div className="content-container">
            <div className="old-document-notice">
              <p>
                This document uses an older format and cannot be edited here.
                <br />
                Please copy the transcript and reprocess the note.
              </p>
            </div>
          </div>
        ) : (
          <div className="content-container">
            <div className="loading-sections">
              <p>Loading document sections...</p>
            </div>
          </div>
        )}

        {/* Transcript tray — sticks to edge, scrolls independently. Gated on !isViewOnly to prevent PHI leak on shared records (Userback #7563915). */}
        {!isViewOnly && (
          <TranscriptTray
            key={`${patientId}:${documentData?.id}`}
            isOpen={isTranscriptOpen}
            position={trayPosition}
            onClose={() => {
              setIsTranscriptOpen(false);
              if (userId) updateUserSettings(userId, { transcriptTrayOpen: false });
            }}
            onTogglePosition={() => {
              setTrayPosition(prev => {
                const next = prev === "right" ? "bottom" : "right";
                if (userId) updateUserSettings(userId, { trayPosition: next });
                return next;
              });
            }}
            documentData={documentData}
            patientId={patientId}
            patientName={personName}
            userId={userId}
            clinicianDisplayName={settings.clinicianDisplayName || ""}
            onClinicianNameChange={(name) => setSettings(prev => ({ ...prev, clinicianDisplayName: name }))}
            settings={settings}
            advancedTranscript={true}
          />
        )}

        {/* Comments margin zone — right side, section-aligned positioning */}
        <CommentMarginZone
          isOpen={isCommentsOpen}
          readOnly={!canUseDocumentActions}
          onClose={() => {
            setIsCommentsOpen(false);
            clearCommentActiveSection();
            if (userId) updateUserSettings(userId, { commentsMarginOpen: false });
          }}
          comments={commentsHook.comments}
          sectionOrder={sectionOrder}
          onAddComment={canUseDocumentActions ? commentsHook.addComment : undefined}
          onEditComment={canUseDocumentActions ? commentsHook.editComment : undefined}
          onDeleteComment={canUseDocumentActions ? commentsHook.deleteComment : undefined}
          currentUserId={userId}
          currentUserRole={currentUser?.role}
          activeSectionKey={commentActiveSectionKey}
          activeSectionTitle={commentActiveSectionTitle}
          pendingAnchor={pendingAnchorRef.current}
          onClearActiveSection={clearCommentActiveSection}
          onStartNewComment={() => {
            if (!canActOnCurrentDocument()) return;
            setCommentActiveSectionKey(null);
            setCommentActiveSectionTitle("General");
            pendingAnchorRef.current = null;
          }}
          contentContainerRef={contentContainerRef}
        />
      </div>

      {/* Modals rendered outside page-container for proper stacking */}
      <ModalPortal isOpen={isExportOpen && canUseDocumentActions}>
        <div className="modal" onClick={() => setIsExportOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close" onClick={() => setIsExportOpen(false)} aria-label="Close export dialog">
              &times;
            </button>
            <ConfirmDocument
              key={`${patientId}/${docId}`}
              documentData={documentData}
              documentType={currentDocumentType}
              patientId={patientId}
              dataHistory={documentHistory}
              docId={docId}
              documentComplete={documentComplete}
              personName={personName}
              riskAssessmentEnabled={flags.riskAssessment !== false && companySettings.riskAssessmentEnabled !== false}
              companyName={companySettings.companyName || ''}
            />
          </div>
        </div>
      </ModalPortal>

      <ArchiveDocumentConfirmModal
        key={`${patientId}/${docId}`}
        isOpen={isDeleteConfirmOpen && canUseDocumentActions}
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={() => handleDeleteDocument(docId)}
      />

      <ModalPortal isOpen={isReprocessConfirmOpen}>
        <div className="modal" onClick={() => setIsReprocessConfirmOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">This document is incomplete. Click Continue to reprocess.</h3>
            <div className="modal-button-container">
              <button className="button-cancel-secondary" onClick={() => setIsReprocessConfirmOpen(false)}>
                Cancel
              </button>
              <button className="button-update" onClick={handleReprocessDocument}>
                Continue
              </button>
            </div>
          </div>
        </div>
      </ModalPortal>
    </div>
  );
};

export default React.memo(Document);
